import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar,
  Plus,
  MoreVertical,
  Settings,
  Trash2,
  Edit,
  Users,
  FileText,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import DashboardLayout from '../components/DashboardLayout';
import { api, isUpgradeError } from '../context/AuthContext';
import { toast } from 'sonner';

const BRAND_COLORS = [
  '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B',
  '#EF4444', '#06B6D4', '#84CC16', '#6366F1', '#14B8A6'
];

const CalendarsPage = () => {
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    brand_color: '#10B981'
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchCalendars();
  }, []);

  const fetchCalendars = async () => {
    try {
      const response = await api.get('/calendars');
      setCalendars(response.data);
    } catch (error) {
      toast.error('Failed to load calendars');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCalendar = async () => {
    try {
      const response = await api.post('/calendars', formData);
      setCalendars([...calendars, response.data]);
      setShowCreateDialog(false);
      setFormData({ name: '', description: '', brand_color: '#10B981' });
      toast.success('Calendar created successfully');
    } catch (error) {
      if (!isUpgradeError(error)) toast.error('Failed to create calendar');
    }
  };

  const handleDeleteCalendar = async () => {
    if (!selectedCalendar) return;
    try {
      await api.delete(`/calendars/${selectedCalendar.calendar_id}`);
      setCalendars(calendars.filter(c => c.calendar_id !== selectedCalendar.calendar_id));
      setShowDeleteDialog(false);
      setSelectedCalendar(null);
      toast.success('Calendar deleted');
    } catch (error) {
      toast.error('Failed to delete calendar');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="calendars-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Calendars</h1>
            <p className="text-slate-600 mt-1">Manage your brand calendars and social accounts</p>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-emerald-500 hover:bg-emerald-600 text-white"
            data-testid="create-calendar-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Calendar
          </Button>
        </div>

        {/* Calendars Grid */}
        {calendars.length === 0 ? (
          <Card className="border-slate-100">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Calendar className="h-16 w-16 text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No calendars yet</h3>
              <p className="text-slate-500 text-center max-w-sm mb-6">
                Create your first calendar to start planning and scheduling content for your brand.
              </p>
              <Button
                onClick={() => setShowCreateDialog(true)}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Calendar
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {calendars.map((calendar, i) => (
              <motion.div
                key={calendar.calendar_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="border-slate-100 hover:border-emerald-200 hover:shadow-lg hover:shadow-emerald-500/5 transition-all group">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${calendar.brand_color}20` }}
                      >
                        <Calendar className="h-6 w-6" style={{ color: calendar.brand_color }} />
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <MoreVertical className="h-4 w-4 text-slate-400" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/calendars/${calendar.calendar_id}/settings`)}>
                            <Settings className="h-4 w-4 mr-2" />
                            Settings
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedCalendar(calendar);
                              setShowDeleteDialog(true);
                            }}
                            className="text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    <Link to={`/calendars/${calendar.calendar_id}`}>
                      <h3 className="text-lg font-semibold text-slate-900 mb-1 hover:text-emerald-600 transition-colors">
                        {calendar.name}
                      </h3>
                    </Link>
                    <p className="text-sm text-slate-500 line-clamp-2 mb-4">
                      {calendar.description || 'No description'}
                    </p>

                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <div className="flex items-center gap-1">
                        <FileText className="h-4 w-4" />
                        <span>{calendar.post_count || 0} posts</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        <span>{Object.keys(calendar.social_accounts || {}).length} accounts</span>
                      </div>
                    </div>

                    {/* Social accounts preview */}
                    {Object.keys(calendar.social_accounts || {}).length > 0 && (
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <div className="flex flex-wrap gap-2">
                          {Object.keys(calendar.social_accounts).map((platform) => (
                            <span
                              key={platform}
                              className="px-2 py-1 text-xs rounded-full bg-slate-100 text-slate-600 capitalize"
                            >
                              {platform}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mt-4">
                      <Link to={`/calendars/${calendar.calendar_id}`}>
                        <Button variant="outline" className="w-full border-slate-200 hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-600">
                          Open Calendar
                          <ExternalLink className="h-4 w-4 ml-2" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Create Calendar Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Calendar</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Calendar Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., My Brand"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="calendar-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description (optional)</Label>
                <Textarea
                  id="description"
                  placeholder="What is this calendar for?"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  data-testid="calendar-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Brand Color</Label>
                <div className="flex flex-wrap gap-2">
                  {BRAND_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, brand_color: color })}
                      className={`w-8 h-8 rounded-lg transition-transform ${
                        formData.brand_color === color ? 'ring-2 ring-offset-2 ring-slate-900 scale-110' : ''
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateCalendar}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                disabled={!formData.name}
                data-testid="create-calendar-submit-btn"
              >
                Create Calendar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Calendar</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{selectedCalendar?.name}"? This will also delete all posts associated with this calendar. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteCalendar}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default CalendarsPage;
